// Anchors a text selection to a DOM container so it can be found again after the
// document around it changes. Uses the W3C Web Annotation quote-selector model:
// the quoted text plus a little surrounding context, with the original offset as a
// tie-breaker. No dependencies - works over any container element.

export const CONTEXT_LENGTH = 32

// Every text node under container, in document order.
export const collectTextNodes = (container) => {
  if (!container) return []
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false)
  const nodes = []
  let node = walker.nextNode()
  while (node) {
    nodes.push(node)
    node = walker.nextNode()
  }
  return nodes
}

// Flattens the container into a single string, keeping a map back to the text nodes.
export const flattenText = (container) => {
  const nodes = collectTextNodes(container)
  const segments = []
  let text = ''
  nodes.forEach((node) => {
    segments.push({ node, start: text.length, end: text.length + node.length })
    text += node.data
  })
  return { text, segments }
}

const offsetToPoint = (segments, offset) => {
  for (const segment of segments) {
    if (offset >= segment.start && offset <= segment.end) return { node: segment.node, offset: offset - segment.start }
  }
  const last = segments[segments.length - 1]
  return last ? { node: last.node, offset: last.node.length } : null
}

const pointToOffset = (segments, node, offset) => {
  const segment = segments.find(candidate => candidate.node === node)
  if (!segment) return null
  return segment.start + offset
}

// Turns a live Range into a portable anchor: { quote, prefix, suffix, startOffset }.
export const describeRange = (container, range) => {
  if (!container || !range || range.collapsed) return null

  const { text, segments } = flattenText(container)
  const start = pointToOffset(segments, range.startContainer, range.startOffset)
  const end = pointToOffset(segments, range.endContainer, range.endOffset)
  if (start === null || end === null || end <= start) return null

  return {
    quote: text.slice(start, end),
    prefix: text.slice(Math.max(0, start - CONTEXT_LENGTH), start),
    suffix: text.slice(end, end + CONTEXT_LENGTH),
    startOffset: start
  }
}

const findAllOccurrences = (text, quote) => {
  const positions = []
  let index = text.indexOf(quote)
  while (index !== -1) {
    positions.push(index)
    index = text.indexOf(quote, index + 1)
  }
  return positions
}

// Higher is better. Context matches dominate, distance from the original offset breaks ties.
const scoreCandidate = ({ text, position, quote, prefix, suffix, startOffset }) => {
  let score = 0

  if (prefix) {
    const actual = text.slice(Math.max(0, position - prefix.length), position)
    if (actual === prefix) score += 100
    else if (actual.endsWith(prefix.slice(-8))) score += 40
  }

  if (suffix) {
    const end = position + quote.length
    const actual = text.slice(end, end + suffix.length)
    if (actual === suffix) score += 100
    else if (actual.startsWith(suffix.slice(0, 8))) score += 40
  }

  if (Number.isFinite(startOffset)) score += Math.max(0, 50 - Math.abs(position - startOffset) / 50)

  return score
}

// Finds the anchor's text in the container again. Returns null when the quote is gone.
export const resolveAnchor = (container, anchor) => {
  if (!container || !anchor?.quote) return null

  const { text, segments } = flattenText(container)
  if (!segments.length) return null

  const positions = findAllOccurrences(text, anchor.quote)
  if (!positions.length) return null

  const best = positions.reduce((winner, position) => {
    const score = scoreCandidate({ text, position, quote: anchor.quote, prefix: anchor.prefix, suffix: anchor.suffix, startOffset: anchor.startOffset })
    return !winner || score > winner.score ? { position, score } : winner
  }, null)

  const startPoint = offsetToPoint(segments, best.position)
  const endPoint = offsetToPoint(segments, best.position + anchor.quote.length)
  if (!startPoint || !endPoint) return null

  const range = document.createRange()
  range.setStart(startPoint.node, startPoint.offset)
  range.setEnd(endPoint.node, endPoint.offset)
  return range
}

// Wraps a range in <mark> elements - one per text node, so ranges that cross
// element boundaries work where surroundContents() would throw.
export const wrapRange = (range, className, dataset = {}) => {
  if (!range || range.collapsed) return []

  const nodes = []
  const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT, null, false)
  let node = walker.nextNode()
  while (node) {
    if (range.intersectsNode(node)) nodes.push(node)
    node = walker.nextNode()
  }
  if (!nodes.length && range.startContainer.nodeType === Node.TEXT_NODE) nodes.push(range.startContainer)

  const marks = []
  nodes.forEach((textNode) => {
    const from = textNode === range.startContainer ? range.startOffset : 0
    const to = textNode === range.endContainer ? range.endOffset : textNode.length
    if (to <= from) return

    let target = textNode
    if (to < target.length) target.splitText(to)
    if (from > 0) target = target.splitText(from)

    const mark = document.createElement('mark')
    mark.className = className
    Object.entries(dataset).forEach(([key, value]) => { mark.dataset[key] = value })
    target.parentNode.insertBefore(mark, target)
    mark.appendChild(target)
    marks.push(mark)
  })

  return marks
}

// Removes every mark previously added by wrapRange and stitches the text back together.
export const clearMarks = (container, className) => {
  if (!container) return
  container.querySelectorAll(`mark.${className}`).forEach((mark) => {
    const parent = mark.parentNode
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
    parent.removeChild(mark)
    parent.normalize()
  })
}
